import "./BookingDetails.scss";
import useDirection from "hooks/useDirection";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import BookingService from "services/booking.service";
import { useEffect, useState } from "react";
import DateService from "utils/date.util";
import { MODAL, modalActions } from "redux/slices/modal";
import PartnerBackButton from "components/atoms/PartnerBackButton";
import YeloPlusLogo from "assets/yeloplus-logo.svg";
import CircularLoader from "components/atoms/CircleLoader";
import {
  getPartnerTokenFromStorage,
  isPartnerWebviewSession,
  withPartnerToken,
  partnerHomePath,
} from "utils/partnerSession.util";
import { store } from "redux/store";
import {
  bookingStatusLabel,
  normalizeBookingStatusKey,
  statusPresentation,
} from "pages/Bookings/BookingsList/bookingCard";
import {
  bookingTimeFormatOf,
  formatTimeForDisplay,
} from "utils/timeFormat.util";
import moment from "moment";

type PageState = "loading" | "ready" | "notfound";

const ClockIcon = () => (
  <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 1.8" />
  </svg>
);

export default function BookingDetails() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  /* The URL's token when it carries one, else the session's own. A link
     without ?token= (a push notification, the web view restoring its page)
     used to land on "Your session has ended" while the session was fine. */
  const token = searchParams.get("token") || getPartnerTokenFromStorage() || null;
  const dispatch = useAppDispatch();
  const storedBooking = useAppSelector((state) => state.booking.booking);
  const [cancelLoading, setCancelLoading] = useState(false);
  /* A booking tapped in the list is already in the store: shown at once,
     and refreshed underneath, instead of a skeleton and a full wait. */
  const [pageState, setPageState] = useState<PageState>(() =>
    storedBooking?._id === id ? "ready" : "loading"
  );
  const [reloadKey, setReloadKey] = useState(0);
  const [reportExpanded, setReportExpanded] = useState(false);
  /* Whether the report message runs past its 4 clamped lines — measured,
     since that depends on the card's width (a phone wraps far sooner than a
     tablet). Show more only appears when there is more to show. */
  const [reportTextEl, setReportTextEl] = useState<HTMLParagraphElement | null>(
    null
  );
  const [reportOverflows, setReportOverflows] = useState(false);

  /* Another booking opened in place (same page, new id) starts collapsed. */
  useEffect(() => {
    setReportExpanded(false);
    setReportOverflows(false);
  }, [id]);

  useEffect(() => {
    if (!reportTextEl || reportExpanded) return;
    /* The text's full height against the 4 lines it is clamped to. The clamp
       is lifted for the reading (no paint happens in between): newer Chrome
       leaves clamped-away lines out of scrollHeight, so the clamped box alone
       cannot tell. Half a line of slack, as Cairo's tall Arabic glyphs run a
       few pixels over a line. */
    const measure = () => {
      /* Expanding fires the observer once more before this effect is torn
         down; re-clamping then would undo the tap. */
      if (!reportTextEl.classList.contains("is-clamped")) return;
      const line = parseFloat(getComputedStyle(reportTextEl).lineHeight) || 20;
      reportTextEl.classList.remove("is-clamped");
      const full = reportTextEl.scrollHeight;
      reportTextEl.classList.add("is-clamped");
      setReportOverflows(full > line * 4 + line / 2);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(reportTextEl);
    return () => observer.disconnect();
  }, [reportTextEl, reportExpanded, storedBooking?.report_a_trip?.report]);
  /* Only trust the store once it holds *this* booking — it can still carry
     the previously opened one while the new request is in flight. */
  const booking = storedBooking?._id === id ? storedBooking : null;

  const { t } = useTranslation();
  const direction = useDirection();

  /* This screen is reached from two places: the bookings list, and the
     payment success screen right after booking. Back has to return to the
     one the reader actually came through, so the caller says which. A
     direct load (a push notification, a shared link) has neither behind it
     and falls back to home. */
  const backTo =
    searchParams.get("from") === "bookings"
      ? withPartnerToken("/bookings")
      : partnerHomePath();

  const openForEdits =
    booking?.current_status === "posted" ||
    booking?.current_status === "accepted";
  /* The backend says whether the booking can still be changed (status, then
     the provider's policy or Yelo's cut-off before pickup) — the rule its
     update enforces, so Edit is never offered for a change that would be
     refused. Older replies without `can_update` keep the status check. */
  const canEditBooking =
    openForEdits &&
    (typeof booking?.can_update === "boolean"
      ? booking.can_update
      : !booking?.isAggregator || booking?.can_update_with_provider !== false);
  /* Still upcoming, but past Yelo's cut-off: say so instead of an Edit
     that would fail. */
  const editsClosed =
    openForEdits && booking?.can_update === false && !!booking?.update_cutoff;

  useEffect(() => {
    if (!isPartnerWebviewSession() || !id || !token) return;
    if (searchParams.get("token")) return;

    /* Keeps `from`, which decides where back goes. */
    const from = searchParams.get("from");
    navigate(
      withPartnerToken(`/booking/${id}${from ? `?from=${from}` : ""}`),
      { replace: true }
    );
  }, [id, navigate, searchParams, token]);

  useEffect(() => {
    if (!id || !token) {
      navigate(withPartnerToken("/not-authorize"), { replace: true });
      return;
    }
    let active = true;
    /* The store holds this booking when it was opened from the list (or
       just paid for): show it, and only fall back to "not found" when there
       is nothing to show — offline, the cached one stays. */
    const cached = store.getState().booking.booking?._id === id;
    setPageState(cached ? "ready" : "loading");
    BookingService.getBookingById(id, token).then(([success]: any) => {
      if (!active) return;
      if (success?.data?.data?.trip) setPageState("ready");
      else if (!cached) setPageState("notfound");
    });
    return () => {
      active = false;
    };
  }, [token, id, navigate, dispatch, reloadKey]);

  const handleReportProblem = () => {
    dispatch(
      modalActions.openModal({
        data: {
          bookingId: id,
          token,
          bookingNumber: booking?.booking_number,
        },
        /* yelo-usernext's report modal: a short form, a narrower card. */
        width: "440px",
        type: MODAL.REPORT_A_PROBLEM,
      })
    );
  };

  const status = normalizeBookingStatusKey(booking?.current_status);
  const statusTone = statusPresentation(status).tone;
  const statusLabel = bookingStatusLabel(booking?.current_status, t);
  const isCancelled = status === "cancelled";
  const isClosed = isCancelled || status === "finished" || status === "noshow";
  const hasDriver = !!booking?.driver_details?.first_name;
  /* The booking's flight number is the top-level field (sent at checkout
     and by the edit modal). Bookings made before 2026-10-19 carried it only
     in additional_info, so that is read as a fallback. */
  /* As in yelo-usernext's booking detail: the booking's top-level flight
     number, shown whenever it has one. */
  const flightNumber = String(booking?.flight_number || "")
    .trim()
    .toUpperCase();
  const pickupSign = booking?.additional_info?.pickup_sign;
  /* The driver's number in full: stored as `phone` plus a separate
     `country_code`, unless the phone already carries its own "+". */
  const driverPhone = (() => {
    const phone = String(booking?.driver_details?.phone || "").trim();
    if (!phone || phone.startsWith("+")) return phone;
    const code = String(booking?.driver_details?.country_code || "").trim();
    if (!code) return phone;
    return `${code.startsWith("+") ? code : `+${code}`} ${phone}`;
  })();
  const chauffeurNotes = String(booking?.additional_info?.notes || "").trim();
  /* The customer's "Report a problem" message, as support sees it in the
     admin panel's Reported Problems. One per booking: a new report replaces
     the last and reopens it. */
  const report = booking?.report_a_trip?.report ? booking.report_a_trip : null;
  const reportResolved = !!report?.isresolved;
  /* createdDate is a real UTC instant (unlike the trip's own date, which is
     stored as zone-local time), so it is shown in the device's own zone. */
  const reportedOn = report?.createdDate
    ? `${moment(report.createdDate).format("ddd, MMM DD")} · ${formatTimeForDisplay(
        moment(report.createdDate).format("HH:mm"),
        bookingTimeFormatOf(booking),
        { am: t("am", "AM"), pm: t("pm", "PM") }
      )}`
    : "";
  const canCancel = ["posted", "accepted", "ongoing", "arrived"].includes(status);
  const currency = booking?.price_details?.currency_sign;

  const topbar = (
    <div className="yp-booking__topbar">
      <PartnerBackButton
        className="yp-booking__back"
        to={backTo}
        pop={searchParams.get("from") === "bookings"}
      />
      <div className="yp-booking__brand">
        <img src={YeloPlusLogo} alt="YeloPlus" />
      </div>
      <span className="yp-booking__topbar-spacer" aria-hidden="true" />
    </div>
  );

  if (pageState === "loading" || (pageState === "ready" && !booking)) {
    return (
      <div className="yp-booking" dir={direction} aria-busy="true">
        {topbar}
        <div className="yp-booking__container" aria-label={String(t("loading"))}>
          <div className="yp-booking__card yp-booking__skeleton">
            <span className="sk sk--sm" />
            <span className="sk sk--lg" />
            <span className="sk sk--pill" />
          </div>
          <div className="yp-booking__card yp-booking__skeleton">
            <span className="sk sk--row" />
            <span className="sk sk--md" />
            <span className="sk sk--row" />
          </div>
          <div className="yp-booking__card yp-booking__skeleton">
            <span className="sk sk--md" />
            <span className="sk sk--row" />
            <span className="sk sk--row" />
          </div>
        </div>
      </div>
    );
  }

  if (pageState === "notfound") {
    return (
      <div className="yp-booking" dir={direction}>
        {topbar}
        <div className="yp-booking__empty" role="alert">
          <span className="yp-booking__empty-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="6.5" />
              <path d="m20 20-4.2-4.2M9 9l4 4m0-4-4 4" />
            </svg>
          </span>
          <h1>{t("bookingNotFoundTitle")}</h1>
          <p>{t("bookingNotFoundBody")}</p>
          <div className="yp-booking__empty-actions">
            <button
              type="button"
              className="yp-booking__primary"
              onClick={() => setReloadKey((key) => key + 1)}
            >
              {t("tryAgain")}
            </button>
            <button
              type="button"
              className="yp-booking__secondary"
              onClick={() => navigate(withPartnerToken("/bookings"))}
            >
              {t("goToMyBookings")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="yp-booking" dir={direction}>
      {topbar}

      <div className="yp-booking__container">
        {/* Booking first: number, status, and the one edit action. */}
        <div className="yp-booking__card yp-booking__hero">
          <div className="yp-booking__hero-top">
            <div>
              <span className="yp-booking__hero-label">{t("bookingId")}</span>
              <h1 className="yp-booking__title">
                {booking?.booking_number ? `#${booking.booking_number}` : "—"}
              </h1>
            </div>
            <span className={`yp-booking__status is-${statusTone}`}>
              <i aria-hidden="true" />
              {statusLabel}
            </span>
          </div>
          {(canEditBooking || canCancel) && (
            <div className="yp-booking__actions">
              {canEditBooking && (
                <button
                  type="button"
                  className="yp-booking__edit"
                  onClick={() =>
                    dispatch(
                      modalActions.openModal({
                        data: { ...booking, token },
                        width: "400px",
                        type: MODAL.UPDATE_BOOKING,
                      })
                    )
                  }
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3Z" />
                    <path d="m13.5 7 3.5 3.5" />
                  </svg>
                  {t("editBooking")}
                </button>
              )}
              {canCancel && (
                <button
                  type="button"
                  className="yp-booking__cancel-btn"
                  disabled={cancelLoading}
                  aria-busy={cancelLoading || undefined}
                  onClick={async () => {
                    if (!token) return;
                    setCancelLoading(true);
                    try {
                      await BookingService.cancelBooking(
                        booking._id,
                        token,
                        bookingTimeFormatOf(booking)
                      );
                    } finally {
                      setCancelLoading(false);
                    }
                  }}
                >
                  {cancelLoading ? (
                    <CircularLoader size={18} color="inherit" />
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="12" cy="12" r="8.5" />
                        <path d="m9 9 6 6m0-6-6 6" />
                      </svg>
                      {t("cancelBooking")}
                    </>
                  )}
                </button>
              )}
            </div>
          )}
          {editsClosed && (
            <p className="yp-booking__edit-closed">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="8.5" />
                <path d="M12 7.5V12l3 2" />
              </svg>
              {t("booking_edits_closed", {
                count: booking.update_cutoff_hours,
              })}
            </p>
          )}
        </div>

        {/* Driver: the assigned chauffeur, or a clear "being assigned" state. */}
        {!isCancelled && hasDriver && (
          <div className="yp-booking__card">
            <p className="yp-booking__driver-label">
              {t("The Driver for the Ride")}
            </p>
            <div className="yp-booking__driver">
              <div className="yp-booking__driver-avatar">
                {booking?.driver_details?.profile_pic?.url ? (
                  <img src={booking.driver_details.profile_pic.url} alt="" />
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="8" r="3.4" />
                    <path d="M5 20c.5-4.2 3-6.4 7-6.4s6.5 2.2 7 6.4" />
                  </svg>
                )}
              </div>
              <div className="yp-booking__driver-copy">
                <p className="yp-booking__driver-name">
                  {booking?.driver_details?.first_name}
                </p>
                {!isClosed && driverPhone && (
                  <a
                    className="yp-booking__driver-phone"
                    href={`tel:${driverPhone.replace(/\s+/g, "")}`}
                    dir="ltr"
                  >
                    {driverPhone}
                  </a>
                )}
                {booking?.vehicle_details?.license_plate && (
                  <p className="yp-booking__driver-vehicle">
                    {booking?.vehicle_details?.model}
                    {" · "}
                    <span className="yp-booking__plate">
                      {booking?.vehicle_details?.license_plate}
                    </span>
                  </p>
                )}
              </div>
              {!isClosed && driverPhone && (
                <a
                  className="yp-booking__call"
                  href={`tel:${driverPhone.replace(/\s+/g, "")}`}
                  aria-label={String(t("callDriver"))}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M6.5 4h3l1.5 4-2 1.3a10 10 0 0 0 5.7 5.7L16 13l4 1.5v3a2 2 0 0 1-2.2 2A15.5 15.5 0 0 1 4.5 6.2 2 2 0 0 1 6.5 4Z" />
                  </svg>
                </a>
              )}
            </div>
          </div>
        )}

        {!isClosed && !hasDriver && (
          <div className="yp-booking__card yp-booking__pending">
            <span className="yp-booking__pending-icon" aria-hidden="true">
              <svg viewBox="0 0 28 24">
                <path d="m5 16 2-5.2a3 3 0 0 1 2.8-2h8.4a3 3 0 0 1 2.8 2L23 16v3H5v-3Z" />
                <path d="M4 15h20M8 19v2m12-2v2" />
              </svg>
            </span>
            <div>
              <strong>{t("Driver will be assigned soon")}</strong>
              <span>{t("driverPendingBody")}</span>
            </div>
          </div>
        )}

        <div className="yp-booking__card">
          <div className="yp-booking__journey-head">
            <span className="yp-booking__class">
              {booking?.class_details?.name}
            </span>
            <span className="yp-booking__when">
              <ClockIcon />
              {DateService.getShortDateString(booking?.date)},{" "}
              {DateService.getDisplayTimeString(
                booking?.date,
                bookingTimeFormatOf(booking)
              )}
            </span>
          </div>

          <div className="yp-booking__leg">
            <span className="yp-booking__pin">
              <span className="yp-booking__pin-line" aria-hidden="true" />
              <b />
            </span>
            <div className="yp-booking__leg-row">
              <strong className="yp-booking__leg-title">
                {booking?.pickup?.title}
              </strong>
              <em className="yp-booking__leg-sub">{booking?.pickup?.address}</em>
            </div>

            <span
              className={`yp-booking__pin is-end${
                booking?.trip_type === "hourly" ? " is-hourly" : ""
              }`}
            >
              <b />
            </span>
            <div className="yp-booking__leg-row">
              <strong className="yp-booking__leg-title">
                {booking?.trip_type === "hourly"
                  ? `${booking?.duration} ${t("hours")}`
                  : booking?.destination?.title}
              </strong>
              <em className="yp-booking__leg-sub">
                {booking?.trip_type === "hourly"
                  ? `${t("incl")} ${booking?.duration * 40} ${t("km")}`
                  : booking?.destination?.address}
              </em>
            </div>
          </div>

        </div>

        {/* Pickup details: what the chauffeur works from at pickup — the
            flight they track, the name on the sign, and the customer's own
            notes — in a card of their own under the route, one row each. */}
        {flightNumber || pickupSign || chauffeurNotes ? (
          <div className="yp-booking__card">
            <span className="yp-booking__kicker">
              {t("pickupDetails", "Pickup details")}
            </span>
            <div className="yp-booking__info">
              {flightNumber ? (
                <div className="yp-booking__info-row">
                  <span className="yp-booking__info-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
                    </svg>
                  </span>
                  <div className="yp-booking__info-copy">
                    <span>{t("yp_flight_number_label")}</span>
                    <b dir="ltr">{flightNumber}</b>
                  </div>
                </div>
              ) : null}
              {pickupSign ? (
                <div className="yp-booking__info-row">
                  <span className="yp-booking__info-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <rect x="3.5" y="6" width="17" height="11" rx="2" />
                      <path d="M7.5 10.5h9M7.5 13.5h5M12 17v3" />
                    </svg>
                  </span>
                  <div className="yp-booking__info-copy">
                    <span>{t("pickUpSign")}</span>
                    <b>{pickupSign}</b>
                  </div>
                </div>
              ) : null}
              {chauffeurNotes ? (
                <div className="yp-booking__info-row">
                  <span className="yp-booking__info-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M6 4h9l4 4v12H6z" />
                      <path d="M14.5 4v4.5H19M9 12.5h6.5M9 15.5h4.5" />
                    </svg>
                  </span>
                  <div className="yp-booking__info-copy">
                    <span>{t("yp_chauffeur_notes_label")}</span>
                    <p>{chauffeurNotes}</p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="yp-booking__card">
          <span className="yp-booking__kicker">{t("summary")}</span>

          {/* The fare as a receipt: each line on a hairline, the total last
              and largest. */}
          <div className="yp-booking__fare">

            {booking?.coupon?.value > 0 ? (
              <>
                <div className="yp-booking__row yp-booking__row--muted">
                  <span>{t("subtotalInclVat")}</span>
                  <b>
                    {((booking?.price || 0) + (booking?.coupon?.value || 0)).toFixed?.(2)}{" "}
                    {currency}
                  </b>
                </div>
                <div className="yp-booking__row yp-booking__row--muted">
                  <span>{t("BooingDiscount")}</span>
                  <b>
                    -{booking?.coupon?.value?.toFixed?.(2)} {currency}
                  </b>
                </div>
              </>
            ) : (
              <>
                {booking?.price_details?.netto_price > 0 && (
                  <div className="yp-booking__row yp-booking__row--muted">
                    <span>{t("netPrices")}</span>
                    <b>
                      {booking?.price_details.netto_price.toFixed?.(2)} {currency}
                    </b>
                  </div>
                )}
                {booking?.price_details?.VAT > 0 && (
                  <div className="yp-booking__row yp-booking__row--muted">
                    <span>
                      {t("bookingVat")} {booking?.price_details.tax}%
                    </span>
                    <b>
                      {booking?.price_details.VAT.toFixed?.(2)} {currency}
                    </b>
                  </div>
                )}
              </>
            )}

            <div className="yp-booking__row yp-booking__total">
              <span className="yp-booking__total-label">
                {t("total")} {t("price")}
                <small>{t("IncludingVAT")}</small>
              </span>
              <span className="yp-booking__total-price">
                {booking?.price?.toFixed?.(2)} {currency}
              </span>
            </div>
          </div>
        </div>

        {/* A report already sent, as a support ticket: where support is with
            it (sent → in review → resolved), when it went, what they wrote,
            and the next step. It takes the help row's place. While it is
            open there is no second report button — a new report would
            overwrite this one — so the way on is Help's call / email. */}
        {report ? (
          <section
            className={`yp-booking__card yp-booking__report${
              reportResolved ? " is-resolved" : ""
            }`}
            aria-labelledby="yp-booking-report-title"
          >
            <div className="yp-booking__report-head">
              <span className="yp-booking__help-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M5 21V4.5h11.5l-1.6 3.8 1.6 3.7H5" />
                </svg>
              </span>
              <span className="yp-booking__help-copy">
                <strong id="yp-booking-report-title">
                  {t("reportSubmittedTitle", "Problem reported")}
                </strong>
                {reportedOn ? <span>{reportedOn}</span> : null}
              </span>
              <span
                className={`yp-booking__status ${
                  reportResolved ? "is-live" : "is-ok"
                }`}
              >
                <i aria-hidden="true" />
                {reportResolved
                  ? t("reportResolved", "Resolved")
                  : t("reportInReview", "In review")}
              </span>
            </div>

            <ol className="yp-booking__steps">
              <li className="is-done">
                <i aria-hidden="true" />
                <span>{t("reportStepSent", "Sent")}</span>
              </li>
              <li
                className={reportResolved ? "is-done" : "is-current"}
                aria-current={reportResolved ? undefined : "step"}
              >
                <i aria-hidden="true" />
                <span>{t("reportInReview", "In review")}</span>
              </li>
              <li
                className={reportResolved ? "is-done" : undefined}
                aria-current={reportResolved ? "step" : undefined}
              >
                <i aria-hidden="true" />
                <span>{t("reportResolved", "Resolved")}</span>
              </li>
            </ol>

            <div className="yp-booking__report-message">
              <span className="yp-booking__report-label">
                {t("reportYourMessage", "Your message")}
              </span>
              <p
                ref={setReportTextEl}
                className={`yp-booking__report-text${
                  reportExpanded ? "" : " is-clamped"
                }`}
              >
                {report.report}
              </p>
              {reportOverflows || reportExpanded ? (
                <button
                  type="button"
                  className="yp-booking__report-more"
                  aria-expanded={reportExpanded}
                  onClick={() => setReportExpanded((open) => !open)}
                >
                  {reportExpanded
                    ? t("showLess", "Show less")
                    : t("showMore", "Show more")}
                </button>
              ) : null}
            </div>

            <p className="yp-booking__report-foot">
              {reportResolved
                ? t(
                    "reportResolvedBody",
                    "Our support team has resolved this report."
                  )
                : t(
                    "reportInReviewBody",
                    "Our support team is looking into it and will get back to you."
                  )}
            </p>

            <button
              type="button"
              className="yp-booking__secondary yp-booking__report-action"
              onClick={
                reportResolved
                  ? handleReportProblem
                  : () => navigate(withPartnerToken("/help"))
              }
            >
              {reportResolved
                ? t("reportAnotherProblem", "Report another problem")
                : t("contactSupport", "Contact support")}
            </button>
          </section>
        ) : (
          /* Help comes after the booking, as a quiet row. */
          <button
            type="button"
            className="yp-booking__card yp-booking__help"
            onClick={handleReportProblem}
          >
            <span className="yp-booking__help-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="8.5" />
                <path d="M9.6 9.4a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.1-2.4 3.5M12 16.8h.01" />
              </svg>
            </span>
            <span className="yp-booking__help-copy">
              <strong>{t("needHelp")}</strong>
              <span>{t("reportProblem")}</span>
            </span>
            <svg className="yp-booking__chevron" viewBox="0 0 24 24" aria-hidden="true">
              <path d="m9 6 6 6-6 6" />
            </svg>
          </button>
        )}

      </div>
    </div>
  );
}
